import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import {
  BaseUserModel,
  UserPermissionsModel,
  UserRolesModel,
} from '../Models/ApplicationModels';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { MasterDataSeriveService } from './master-data-serive.service';
import { jwtDecode } from 'jwt-decode';
import { Apps } from '../enum/enum';

@Injectable({
  providedIn: 'root',
})
export class UserserviceService {
  constructor(
    private cookieService: CookieService,
    private route: Router,
    private masterservice: MasterDataSeriveService,
  ) {}

  LoginUrl: string = environment.companyWebBaseUrl;
  public userRoles: UserRolesModel[] = [];
  public userPermissions: UserPermissionsModel[] | null = null;
  public rolesLoaded = false;

  async getSessionStorageItem() {
    const accessToken = this.cookieService.get('accessToken');
    const refreshToken = this.cookieService.get('refreshToken');
    if (!accessToken && !refreshToken) {
      return;
    }
    if (!this.isInternalUser(accessToken)) {
      this.denyExternalUser();
      return;
    }

    this.saveUserInfo(accessToken); // save user info

    // Clear any previous logout event cookie
    document.cookie = `logoutEvent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/;
      Domain=.${environment.domainName}; Secure; SameSite=None`;
  }

  getAuthToken(): string | null {
    return this.cookieService.get('accessToken') || null;
  }

  /**
   * ProductionSystem is an internal staff portal. External accounts
   * (consignors, donors) have their own portals, so an External-origin SSO
   * token must never open a session here even though the cookie is visible on
   * the shared domain.
   */
  isInternalUser(token: string | null): boolean {
    if (!token) {
      return false;
    }
    return this.decodeJwt(token)?.userorigin === 'Internal';
  }

  /** Drop any session this token may have seeded and bounce to Company login. */
  denyExternalUser(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('userDetails');
    window.location.replace(`${this.LoginUrl}/login`);
  }

  getRefreshToken(): string | null {
    return this.cookieService.get('refreshToken') || null;
  }

  decodeJwt(token: string): any {
    try {
      const decodedToken = jwtDecode(token);

      const cleanedToken = this.removeCustomPrefix(decodedToken);

      return cleanedToken;
    } catch (error) {
      console.error('Invalid token:', error);
      return null;
    }
  }

  removeCustomPrefix(tokenData: Record<string, any>): Record<string, any> {
    const cleanedData: Record<string, any> = {};

    Object.keys(tokenData).forEach((key) => {
      const newKey = key.startsWith('custom:')
        ? key.replace('custom:', '')
        : key;
      cleanedData[newKey] = tokenData[key];
    });

    return cleanedData;
  }

  saveUserInfo(token: string) {
    const decodedToken = this.decodeJwt(token);
    if (decodedToken) {
      const { userid, companyid, fullname, role } = decodedToken;
      const user = new BaseUserModel();
      user.userId = userid;
      user.companyId = companyid;
      user.fullName = fullname;
      user.roles = role;

      const user1 = JSON.stringify(user);
      localStorage.setItem('userDetails', user1);
    }
  }

  getUserFromLocalStorage(): BaseUserModel | null {
    const userDetails = localStorage.getItem('userDetails');
    if (userDetails) {
      try {
        return JSON.parse(userDetails) as BaseUserModel;
      } catch (error) {
        console.error('Error parsing userDetails from localStorage:', error);
        return null;
      }
    }
    return null;
  }

  async getUserRolesandPerm(): Promise<UserPermissionsModel[]> {
    if (this.rolesLoaded && this.userPermissions) {
      return this.userPermissions;
    }

    let userData: any | null;
    let user = localStorage.getItem('userDetails');
    if (user) {
      userData = JSON.parse(user) as BaseUserModel;
    } else {
      userData = this.masterservice.getUserDetailsFromStorage();
    }

    if (userData) {
      const data = await this.masterservice
        .getUserRoleDetails(userData.userId)
        .toPromise();
      if (data) {
        this.userRoles = data as UserRolesModel[];
        const appRoles = this.userRoles;
        const userPermissions: UserPermissionsModel[] = [];
        appRoles?.forEach((role) => {
          userPermissions.push(...role.permissions);
        });

        this.userPermissions = userPermissions;
        this.rolesLoaded = true;

        return userPermissions;
      }
    }

    return [];
  }

  transformResponse(newResponse: any): any {
    const oldResponse = {
      email: newResponse.email,
      brijjusername: newResponse.username || '',
      userName: `${newResponse.firstName} ${newResponse.middleName || ''} ${
        newResponse.lastName
      }`.trim(),
      company: newResponse.company?.name || null,
      userRole: this.userRoles?.[0]?.name || 'User',
      userId: newResponse.id,
      companyID: newResponse.company?.id || 0,
      defaultModuleID: newResponse.defaultModuleId,
      userSessionTimeout: newResponse.company.tpmUserSessionTimeout || null,
      storeId: newResponse.defaultStoreId || 0,
      productionAutoPrint: newResponse.productionAutoPrint,
      productionStayScreen: newResponse.productionStayScreen,
      productionLimit: newResponse.productionLimit || null,
      productionScheduleType: newResponse.productionScheduleType ?? null,
      fullName: `${newResponse.firstName} ${newResponse.middleName || ''} ${
        newResponse.lastName
      }`.trim(),
      isNewUser: false,
      isActive: newResponse.status === 1,
      productionCycle: newResponse?.currentWeekCycle?.productionDisplayName,
      productionCycleColor: newResponse?.currentWeekCycle?.productionColor,
      isEnableSubcategory: false,
      displayName: newResponse.displayName,
      profileImgUrl: newResponse.profileFileUrl,
      appID: newResponse.apps?.[0]?.id || null,
      outletID: newResponse.defaultOutletId || 0,
      apps: newResponse.apps,
      defaultProcessorLocationID: newResponse?.defaultStoreLocationId || 0,
      isTpmPresent: !!newResponse.apps?.find(
        (app: { id: number }) => app.id == Apps.TPMPortal,
      ),
      isPosPresent: !!newResponse.apps?.find(
        (app: { id: number }) => app.id == Apps.POS,
      ),
      isDonationPresent: !!newResponse.apps?.find(
        (app: { id: number }) => app.id == Apps.Donation,
      ),
      isListerPresent: !!newResponse.apps?.find(
        (app: { id: number }) => app.id == Apps.Lister,
      ),
      isInventoryPresent: !!newResponse.apps?.find(
        (app: { id: number }) => app.id == Apps.IMS,
      ),
      isBypassLicenses: newResponse.company?.isBypassLicenses ?? false,
      hasAccessToAdminModule: newResponse?.hasAccessToAdminModule ?? false,
      printPreview: newResponse?.company?.showPrintPreview ?? false,
      todaysTotalHoursLoggedIn: newResponse.todaysTotalHoursLoggedIn ?? 0,
    };

    return oldResponse;
  }

  LogOut() {
    localStorage.clear();
    window.location.replace(this.LoginUrl);
  }
}
